import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import PharmacyDutyView from '@/components/PharmacyDutyView';
import { ArticleDetail } from '@/components/ArticleDetail';
import { ArrowLeft, ChevronRight, Phone, ExternalLink, Cross, BookOpen, Users, ShieldAlert, Loader2, User, LogOut, Shield, Utensils, CalendarDays, Waves, Smartphone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

const InfoPage = () => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'menu'); // menu | pharmacies | blog | blog-article | about | emergencies | emergency-article
  const [activeArticle, setActiveArticle] = useState(null);
  const [blogCat, setBlogCat] = useState(null);

  // Scroll to top when view or article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, activeArticle]);

  useEffect(() => {
    api.getArticles().then(res => {
      setArticles(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getField = (obj, field) => obj[`${field}_${language}`] || obj[`${field}_es`] || '';

  // Find "Sobre Nosotros" article
  const aboutArticle = articles.find(a => a.category === 'sobre_nosotros') || null;
  // Emergency articles
  const emergencyArticles = articles.filter(a => a.category === 'emergencias');
  // Blog = all articles except sobre_nosotros and emergencias
  const blogArticles = articles.filter(a => a.category !== 'sobre_nosotros' && a.category !== 'emergencias');

  const PHARMACY_ZONES = [
    {
      zone_es: 'Zona Metropolitana', zone_en: 'Metropolitan Area', zone_de: 'Metropolitangebiet',
      areas: [
        { name: 'Santa Cruz', url: 'https://farmaciatenerife.com/farmacias/santa-cruz-de-tenerife-farmacia-de-guardia.pdf' },
        { name: 'La Laguna', url: 'https://farmaciatenerife.com/farmacias/san-cristobal-de-la-laguna-farmacia-de-guardia.pdf' },
        { name: 'La Cuesta', url: 'https://farmaciatenerife.com/farmacias/la-cuesta-farmacia-de-guardia.pdf' },
        { name: 'Taco', url: 'https://farmaciatenerife.com/farmacias/taco-farmacia-de-guardia.pdf' },
      ]
    },
    {
      zone_es: 'Zona Norte', zone_en: 'North Zone', zone_de: 'Nordzone',
      areas: [
        { name: 'Puerto de la Cruz', url: 'https://farmaciatenerife.com/farmacias/puerto-de-la-cruz-farmacia-de-guardia.pdf' },
        { name: 'La Orotava / Los Realejos', url: 'https://farmaciatenerife.com/farmacias/la-orotava-los-realejos-farmacia-de-guardia.pdf' },
        { name: 'Icod de los Vinos', url: 'https://farmaciatenerife.com/farmacias/icod-de-los-vinos-farmacia-de-guardia.pdf' },
        { name: 'Tacoronte / El Sauzal', url: 'https://farmaciatenerife.com/farmacias/tacoronte-el-sauzal-farmacia-de-guardia.pdf' },
      ]
    },
    {
      zone_es: 'Zona Sur', zone_en: 'South Zone', zone_de: 'Sudzone',
      areas: [
        { name: 'Adeje / Arona', url: 'https://farmaciatenerife.com/farmacias/adeje-y-arona-farmacia-de-guardia.pdf' },
        { name: 'Granadilla / San Miguel', url: 'https://farmaciatenerife.com/farmacias/san-miguel-y-granadilla-farmacia-de-guardia.pdf' },
        { name: 'Guia de Isora / Santiago del Teide', url: 'https://farmaciatenerife.com/farmacias/guia-de-isora-santiago-del-teide-farmacia-de-guardia.pdf' },
      ]
    },
  ];

  // ===== ARTICLE DETAIL VIEW =====
  const renderArticleDetail = (article, backFn) => <ArticleDetail article={article} onBack={backFn} />;

  // ===== ARTICLES LIST VIEW =====
  const BLOG_CATS = {
    gastronomia: { color: '#C4620C', Icon: Utensils, es: 'Gastronomía', en: 'Gastronomy', de: 'Gastronomie' },
    eventos: { color: '#7B2D3A', Icon: CalendarDays, es: 'Eventos', en: 'Events', de: 'Veranstaltungen' },
    playas: { color: '#0E7490', Icon: Waves, es: 'Playas', en: 'Beaches', de: 'Strände' },
    apps: { color: '#38B2AC', Icon: Smartphone, es: 'Apps', en: 'Apps', de: 'Apps' },
  };
  const catMeta = (slug) => BLOG_CATS[slug] || { color: '#6B7280', Icon: BookOpen, es: slug, en: slug, de: slug };
  const isNew = (a) => a.published_at && (Date.now() - new Date(a.published_at).getTime()) < 30 * 86400000;
  const newLabel = { es: 'Nuevo', en: 'New', de: 'Neu' }[language] || 'Nuevo';
  const allLabel = { es: 'Todos', en: 'All', de: 'Alle' }[language] || 'Todos';

  const renderArticlesList = (title, articlesList, backFn, withFilters = false) => {
    const cats = withFilters ? [...new Set(articlesList.map(a => a.category))] : [];
    const shown = withFilters && blogCat ? articlesList.filter(a => a.category === blogCat) : articlesList;
    return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="articles-list">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 safe-top">
        <button onClick={backFn} className="p-1" data-testid="list-back-btn">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="font-bold text-lg text-[#1D1D1F]">{title}</h1>
      </div>
      {cats.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 pt-4 scrollbar-hide" data-testid="blog-category-filters">
          <button onClick={() => setBlogCat(null)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${!blogCat ? 'bg-[#1D1D1F] text-white border-[#1D1D1F]' : 'bg-white text-gray-600 border-gray-200'}`} data-testid="blog-filter-all">{allLabel}</button>
          {cats.map(slug => { const m = catMeta(slug); const active = blogCat === slug; return (
            <button key={slug} onClick={() => setBlogCat(active ? null : slug)}
              className="px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors"
              style={active ? { backgroundColor: m.color, borderColor: m.color, color: '#fff' } : { backgroundColor: '#fff', borderColor: `${m.color}55`, color: m.color }}
              data-testid={`blog-filter-${slug}`}>{m[language] || m.es}</button>
          ); })}
        </div>
      )}
      <div className="px-4 py-4 space-y-3">
        {shown.map(article => { const m = catMeta(article.category); return (
          <div key={article.id} onClick={() => { setActiveArticle(article); }}
            className="flex gap-4 bg-white p-3 shadow-sm border border-gray-50 cursor-pointer active:scale-[0.98] transition-transform"
            data-testid={`article-${article.id}`}>
            {article.image ? (
              <img src={article.image} alt={getField(article, 'title')} className="w-24 h-24 object-cover flex-shrink-0" />
            ) : (
              <div className="w-24 h-24 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: m.color }} data-testid={`article-placeholder-${article.id}`}>
                <m.Icon size={34} className="text-white/90" strokeWidth={1.6} />
              </div>
            )}
            <div className="flex-1 min-w-0 py-1">
              <div className="flex items-center gap-2 mb-1">
                {isNew(article) && <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-[#38B2AC] px-1.5 py-0.5 rounded" data-testid={`article-new-${article.id}`}>{newLabel}</span>}
                <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: m.color }}>{m[language] || m.es}</span>
              </div>
              <h3 className="font-semibold text-sm text-[#1D1D1F] mb-1 line-clamp-2">{getField(article, 'title')}</h3>
              <p className="text-xs text-gray-500 line-clamp-3">{getField(article, 'summary')}</p>
            </div>
            <ChevronRight size={18} className="text-gray-300 self-center flex-shrink-0" />
          </div>
        ); })}
        {shown.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {language === 'es' ? 'No hay articulos disponibles' : 'No articles available'}
          </p>
        )}
      </div>
    </div>
    );
  };

  // ===== ROUTING =====
  if (view === 'menu') {
    return <Navigate to="/more" replace />;
  }

  if (view === 'pharmacies') {
    return <PharmacyDutyView language={language} onBack={() => navigate('/more')} pdfZones={PHARMACY_ZONES} />;
  }

  if (view === 'blog' && activeArticle) {
    return renderArticleDetail(activeArticle, () => setActiveArticle(null));
  }

  if (view === 'blog') {
    return renderArticlesList(
      'Blog',
      blogArticles,
      () => navigate('/more'),
      true
    );
  }

  if (view === 'about' && aboutArticle) {
    return renderArticleDetail(aboutArticle, () => navigate('/more'));
  }

  if (view === 'about') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] pb-20">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 safe-top">
          <button onClick={() => navigate('/more')} className="p-1"><ArrowLeft size={20} className="text-gray-700" /></button>
          <h1 className="font-bold text-lg text-[#1D1D1F]">{language === 'es' ? 'Sobre Nosotros' : 'About Us'}</h1>
        </div>
        <div className="px-5 py-8 text-center text-gray-500">
          {language === 'es' ? 'Proximamente...' : 'Coming soon...'}
        </div>
      </div>
    );
  }

  if (view === 'emergencies' && activeArticle) {
    return renderArticleDetail(activeArticle, () => setActiveArticle(null));
  }

  if (view === 'emergencies') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="emergencies-page">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 flex items-center gap-3 px-4 py-3 safe-top">
          <button onClick={() => navigate('/more')} className="p-1" data-testid="emergencies-back-btn">
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <h1 className="font-bold text-lg text-[#1D1D1F]">
            {language === 'es' ? 'Emergencias' : language === 'de' ? 'Notfalle' : 'Emergencies'}
          </h1>
        </div>
        {/* Emergency call banner */}
        <div className="mx-4 mt-4 mb-4">
          <a href="tel:112"
            className="flex items-center justify-center gap-3 bg-red-500 text-white py-4 rounded-2xl font-bold text-lg shadow-md"
            data-testid="emergency-112-btn">
            <Phone size={22} /> 112 - {language === 'es' ? 'Emergencias' : 'Emergency'}
          </a>
        </div>
        <div className="px-4 space-y-3">
          {emergencyArticles.map(article => (
            <div key={article.id} onClick={() => setActiveArticle(article)}
              className="flex gap-4 bg-white p-3 shadow-sm border border-gray-50 cursor-pointer active:scale-[0.98] transition-transform"
              data-testid={`emergency-article-${article.id}`}>
              {article.image && (
                <img src={article.image} alt={getField(article, 'title')} className="w-24 h-24 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0 py-1">
                <h3 className="font-semibold text-sm text-[#1D1D1F] mb-1 line-clamp-2">{getField(article, 'title')}</h3>
                <p className="text-xs text-gray-500 line-clamp-3">{getField(article, 'summary')}</p>
              </div>
              <ChevronRight size={18} className="text-gray-300 self-center flex-shrink-0" />
            </div>
          ))}
          {emergencyArticles.length === 0 && (
            <p className="text-center text-gray-400 py-8">
              {language === 'es' ? 'Proximamente informacion de emergencias' : 'Emergency info coming soon'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ===== MAIN MENU =====
  const menuTitles = {
    es: { title: 'Menu', subtitle: 'Todo lo que necesitas saber sobre Tenerife' },
    en: { title: 'Menu', subtitle: 'Everything you need to know about Tenerife' },
    de: { title: 'Menu', subtitle: 'Alles, was Sie uber Teneriffa wissen mussen' },
  };
  const t = menuTitles[language] || menuTitles.es;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#38B2AC]" size={32} />
      </div>
    );
  }

  const menuItems = [
    {
      id: 'pharmacies',
      icon: Cross,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      borderColor: 'border-emerald-100',
      title: { es: 'Farmacias de Guardia', en: 'Duty Pharmacies', de: 'Notdienstapotheken' },
      subtitle: { es: 'Encuentra la farmacia abierta mas cercana', en: 'Find the nearest open pharmacy', de: 'Finden Sie die nachste offene Apotheke' },
    },
    {
      id: 'blog',
      icon: BookOpen,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-100',
      title: { es: 'Blog', en: 'Blog', de: 'Blog' },
      subtitle: { es: `${blogArticles.length} articulos sobre gastronomia, playas y mas`, en: `${blogArticles.length} articles about gastronomy, beaches and more`, de: `${blogArticles.length} Artikel uber Gastronomie, Strande und mehr` },
    },
    {
      id: 'about',
      icon: Users,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-100',
      title: { es: 'Sobre Nosotros', en: 'About Us', de: 'Uber uns' },
      subtitle: { es: 'Conoce al equipo detras de Calma Guide', en: 'Meet the team behind Calma Guide', de: 'Lernen Sie das Team hinter Calma Guide kennen' },
    },
    {
      id: 'emergencies',
      icon: ShieldAlert,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      borderColor: 'border-red-100',
      title: { es: 'Emergencias', en: 'Emergencies', de: 'Notfalle' },
      subtitle: { es: 'Numeros de emergencia y hospitales', en: 'Emergency numbers and hospitals', de: 'Notrufnummern und Krankenhauser' },
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="menu-page">
      <div className="px-5 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-[#1D1D1F]">{t.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
      </div>

      <div className="px-4 space-y-3">
        {menuItems.map(item => {
          const Icon = item.icon;
          const content = (
            <div
              className={`flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border ${item.borderColor} cursor-pointer active:scale-[0.98] transition-transform`}
              data-testid={`menu-${item.id}`}
            >
              <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={22} className={item.iconColor} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-[#1D1D1F] text-base">{item.title[language] || item.title.es}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{item.subtitle[language] || item.subtitle.es}</p>
              </div>
              <ChevronRight size={20} className="text-gray-300 flex-shrink-0" />
            </div>
          );

          if (item.link) {
            return (
              <Link key={item.id} to={item.link}>
                {content}
              </Link>
            );
          }

          return (
            <div key={item.id} onClick={() => setView(item.id)}>
              {content}
            </div>
          );
        })}
      </div>

      {/* Mi Cuenta / Iniciar Sesion */}
      <div className="px-4 mt-6 mb-4">
        {user ? (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="menu-account">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#38B2AC]/20 flex items-center justify-center">
                <User size={20} className="text-[#38B2AC]" />
              </div>
              <div>
                <p className="font-semibold text-[#1D1D1F] text-sm">{user.name || user.email}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#38B2AC]/10 text-[#319795] rounded-xl text-sm font-medium"
                  data-testid="menu-admin-btn"
                >
                  <Shield size={16} />
                  Admin
                </button>
              )}
              <button
                onClick={() => { logout(); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium"
                data-testid="menu-logout-btn"
              >
                <LogOut size={16} />
                {language === 'es' ? 'Cerrar sesion' : language === 'de' ? 'Abmelden' : 'Log out'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => navigate('/auth')}
            className="w-full flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer active:scale-[0.98] transition-transform"
            data-testid="menu-login-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
              <User size={22} className="text-gray-500" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <h2 className="font-bold text-[#1D1D1F] text-base">
                {language === 'es' ? 'Iniciar Sesion' : language === 'de' ? 'Anmelden' : 'Log In'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {language === 'es' ? 'Accede a tu cuenta y favoritos' : language === 'de' ? 'Zugang zu Konto und Favoriten' : 'Access your account and favorites'}
              </p>
            </div>
            <ChevronRight size={20} className="text-gray-300 flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
};

export default InfoPage;
